/**
 * Universal Design System
 * 多様なユーザーニーズに対応するユニバーサルデザインシステム
 */

import { webglLogger } from '@/lib/utils/logger'

export interface UserProfile {
  /** ユーザーID */
  id: string
  /** 年齢層 */
  ageGroup: 'child' | 'teen' | 'adult' | 'senior'
  /** 技術レベル */
  techLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  /** 運動機能 */
  motorAbility: 'full' | 'limited' | 'assistive'
  /** 視覚能力 */
  visualAbility: 'full' | 'low-vision' | 'blind' | 'color-blind'
  /** 聴覚能力 */
  auditoryAbility: 'full' | 'hard-of-hearing' | 'deaf'
  /** 認知特性 */
  cognitiveProfile: 'typical' | 'adhd' | 'dyslexia' | 'autism' | 'dementia'
  /** デバイスタイプ */
  deviceType: 'desktop' | 'tablet' | 'mobile' | 'assistive'
  /** 使用環境 */
  environment: 'quiet' | 'noisy' | 'bright' | 'dark' | 'mobile'
  /** 言語設定 */
  language: string
  /** カスタム設定 */
  customSettings: Map<string, unknown>
}

export interface AdaptiveLayout {
  /** レイアウトID */
  id: string
  /** 対象プロファイル */
  targetProfiles: string[]
  /** コンポーネント配置 */
  componentPlacement: Map<string, ComponentPlacement>
  /** スタイル設定 */
  styles: AdaptiveStyles
  /** インタラクション設定 */
  interactions: InteractionSettings
}

export interface ComponentPlacement {
  /** 位置 */
  position: { x: number; y: number }
  /** サイズ */
  size: { width: number; height: number }
  /** 可視性 */
  visible: boolean
  /** 優先度 */
  priority: number
  /** グループ */
  group?: string
}

export interface AdaptiveStyles {
  /** フォントサイズ */
  fontSize: number
  /** 行間 */
  lineHeight: number
  /** コントラスト比 */
  contrastRatio: number
  /** 色パレット */
  colorPalette: string[]
  /** アニメーション速度 */
  animationSpeed: number
  /** 間隔 */
  spacing: number
  /** 境界線の太さ */
  borderWidth: number
  /** フォーカスインジケーターの色 */
  focusIndicatorColor: string
  /** フォーカスインジケーターの太さ */
  focusIndicatorWidth: number
}

export interface InteractionSettings {
  /** タッチターゲットサイズ */
  touchTargetSize: number
  /** ホバー遅延 */
  hoverDelay: number
  /** ダブルタップ遅延 */
  doubleTapDelay: number
  /** スワイプ感度 */
  swipeSensitivity: number
  /** キーボードナビゲーション */
  keyboardNavigation: boolean
  /** 音声制御 */
  voiceControl: boolean
  /** ジェスチャー認識 */
  gestureRecognition: boolean
}

export interface AccessibilityFeature {
  /** 機能ID */
  id: string
  /** 機能名 */
  name: string
  /** 説明 */
  description: string
  /** 対象条件 */
  targetConditions: string[]
  /** 有効化関数 */
  enable: () => void
  /** 無効化関数 */
  disable: () => void
  /** 設定関数 */
  configure: (options: Record<string, unknown>) => void
}

export interface MultimodalFeedback {
  /** 視覚フィードバック */
  visual?: {
    color?: string
    animation?: string
    icon?: string
    text?: string
  }
  /** 音響フィードバック */
  audio?: {
    sound?: string
    speech?: string
    volume?: number
  }
  /** 触覚フィードバック */
  haptic?: {
    pattern?: number[]
    intensity?: number
  }
}

/**
 * ユニバーサルデザインシステム
 */
export class UniversalDesignSystem {
  private userProfiles: Map<string, UserProfile> = new Map()
  private adaptiveLayouts: Map<string, AdaptiveLayout> = new Map()
  private accessibilityFeatures: Map<string, AccessibilityFeature> = new Map()
  private currentUserId: string | null = null
  private currentLayout: AdaptiveLayout | null = null
  private callbacks: Map<string, (data: unknown) => void> = new Map()

  constructor() {
    this.initializeDefaultLayouts()
    this.initializeAccessibilityFeatures()
    this.setupEnvironmentDetection()

    webglLogger.info('[UniversalDesignSystem] Initialized')
  }

  /**
   * デフォルトレイアウトを初期化
   */
  private initializeDefaultLayouts(): void {
    // 高齢者向けレイアウト
    this.addAdaptiveLayout({
      id: 'senior-friendly',
      targetProfiles: ['senior'],
      componentPlacement: new Map([
        ['main-controls', {
          position: { x: 0.1, y: 0.1 },
          size: { width: 0.8, height: 0.2 },
          visible: true,
          priority: 1
        }],
        ['secondary-controls', {
          position: { x: 0.1, y: 0.4 },
          size: { width: 0.8, height: 0.15 },
          visible: true,
          priority: 2
        }],
        ['canvas', {
          position: { x: 0.1, y: 0.6 },
          size: { width: 0.8, height: 0.35 },
          visible: true,
          priority: 0
        }]
      ]),
      styles: {
        fontSize: 24,
        lineHeight: 1.8,
        contrastRatio: 7.0,
        colorPalette: ['#000000', '#ffffff', '#0066cc', '#ff6600'],
        animationSpeed: 0.3,
        spacing: 24,
        borderWidth: 3,
        focusIndicatorColor: '#ffff00',
        focusIndicatorWidth: 3
      },
      interactions: {
        touchTargetSize: 48,
        hoverDelay: 500,
        doubleTapDelay: 800,
        swipeSensitivity: 0.3,
        keyboardNavigation: true,
        voiceControl: true,
        gestureRecognition: false
      }
    })

    // 視覚障害者向けレイアウト
    this.addAdaptiveLayout({
      id: 'visual-impaired',
      targetProfiles: ['blind', 'low-vision'],
      componentPlacement: new Map([
        ['audio-controls', {
          position: { x: 0, y: 0 },
          size: { width: 1, height: 0.3 },
          visible: true,
          priority: 0
        }],
        ['keyboard-shortcuts', {
          position: { x: 0, y: 0.3 },
          size: { width: 1, height: 0.4 },
          visible: true,
          priority: 1
        }],
        ['status-info', {
          position: { x: 0, y: 0.7 },
          size: { width: 1, height: 0.3 },
          visible: true,
          priority: 2
        }]
      ]),
      styles: {
        fontSize: 18,
        lineHeight: 2.0,
        contrastRatio: 21.0,
        colorPalette: ['#000000', '#ffffff', '#ffff00', '#ff0000'],
        animationSpeed: 0.1,
        spacing: 32,
        borderWidth: 4,
        focusIndicatorColor: '#ffff00',
        focusIndicatorWidth: 4
      },
      interactions: {
        touchTargetSize: 56,
        hoverDelay: 200,
        doubleTapDelay: 600,
        swipeSensitivity: 0.2,
        keyboardNavigation: true,
        voiceControl: true,
        gestureRecognition: true
      }
    })

    // モバイル向けレイアウト
    this.addAdaptiveLayout({
      id: 'mobile-optimized',
      targetProfiles: ['mobile'],
      componentPlacement: new Map([
        ['touch-controls', {
          position: { x: 0, y: 0.8 },
          size: { width: 1, height: 0.2 },
          visible: true,
          priority: 1
        }],
        ['canvas', {
          position: { x: 0, y: 0 },
          size: { width: 1, height: 0.7 },
          visible: true,
          priority: 0
        }],
        ['mini-controls', {
          position: { x: 0.85, y: 0.02 },
          size: { width: 0.13, height: 0.15 },
          visible: true,
          priority: 2
        }]
      ]),
      styles: {
        fontSize: 16,
        lineHeight: 1.4,
        contrastRatio: 4.5,
        colorPalette: ['#1a1a1a', '#ffffff', '#007acc', '#28a745'],
        animationSpeed: 0.8,
        spacing: 16,
        borderWidth: 1,
        focusIndicatorColor: '#007acc',
        focusIndicatorWidth: 2
      },
      interactions: {
        touchTargetSize: 44,
        hoverDelay: 0,
        doubleTapDelay: 300,
        swipeSensitivity: 0.6,
        keyboardNavigation: false,
        voiceControl: false,
        gestureRecognition: true
      }
    })

    // 子供向けレイアウト
    this.addAdaptiveLayout({
      id: 'child-friendly',
      targetProfiles: ['child'],
      componentPlacement: new Map([
        ['large-buttons', {
          position: { x: 0.05, y: 0.1 },
          size: { width: 0.9, height: 0.25 },
          visible: true,
          priority: 0
        }],
        ['canvas', {
          position: { x: 0.05, y: 0.4 },
          size: { width: 0.9, height: 0.5 },
          visible: true,
          priority: 1
        }],
        ['simple-info', {
          position: { x: 0.05, y: 0.92 },
          size: { width: 0.9, height: 0.06 },
          visible: true,
          priority: 2
        }]
      ]),
      styles: {
        fontSize: 20,
        lineHeight: 1.6,
        contrastRatio: 5.0,
        colorPalette: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#a55eea'],
        animationSpeed: 1.2,
        spacing: 20,
        borderWidth: 2,
        focusIndicatorColor: '#ffff00',
        focusIndicatorWidth: 3
      },
      interactions: {
        touchTargetSize: 60,
        hoverDelay: 300,
        doubleTapDelay: 500,
        swipeSensitivity: 0.4,
        keyboardNavigation: true,
        voiceControl: true,
        gestureRecognition: true
      }
    })
  }

  /**
   * アクセシビリティ機能を初期化
   */
  private initializeAccessibilityFeatures(): void {
    // 音声ナビゲーション
    this.addAccessibilityFeature({
      id: 'voice-navigation',
      name: '音声ナビゲーション',
      description: '音声コマンドでインターフェースを操作',
      targetConditions: ['blind', 'low-vision', 'motor-limited'],
      enable: () => this.enableVoiceNavigation(),
      disable: () => this.disableVoiceNavigation(),
      configure: (options) => this.configureVoiceNavigation(options)
    })

    // 高コントラスト表示
    this.addAccessibilityFeature({
      id: 'high-contrast',
      name: '高コントラスト表示',
      description: '視認性を向上させる高コントラストモード',
      targetConditions: ['low-vision', 'senior'],
      enable: () => this.enableHighContrast(),
      disable: () => this.disableHighContrast(),
      configure: (options) => this.configureHighContrast(options)
    })

    // 大きなテキスト
    this.addAccessibilityFeature({
      id: 'large-text',
      name: '大きなテキスト',
      description: 'フォントサイズを大きくして読みやすさを向上',
      targetConditions: ['senior', 'low-vision'],
      enable: () => this.enableLargeText(),
      disable: () => this.disableLargeText(),
      configure: (options) => this.configureLargeText(options)
    })

    // スローアニメーション
    this.addAccessibilityFeature({
      id: 'slow-animations',
      name: 'スローアニメーション',
      description: 'アニメーション速度を遅くして追跡しやすく',
      targetConditions: ['senior', 'adhd', 'cognitive-impaired'],
      enable: () => this.enableSlowAnimations(),
      disable: () => this.disableSlowAnimations(),
      configure: (options) => this.configureSlowAnimations(options)
    })

    // 触覚フィードバック
    this.addAccessibilityFeature({
      id: 'haptic-feedback',
      name: '触覚フィードバック',
      description: '操作時の振動フィードバック',
      targetConditions: ['deaf', 'hard-of-hearing', 'blind'],
      enable: () => this.enableHapticFeedback(),
      disable: () => this.disableHapticFeedback(),
      configure: (options) => this.configureHapticFeedback(options)
    })
  }

  /**
   * 環境検出を設定
   */
  private setupEnvironmentDetection(): void {
    // 画面サイズ検出
    const checkScreenSize = () => {
      const width = window.innerWidth
      const height = window.innerHeight

      if (width < 768) {
        this.detectMobileEnvironment()
      } else if (width > 1920 && height > 1080) {
        this.detectHighResolutionEnvironment()
      }
    }

    // 明度検出
    const checkAmbientLight = () => {
      if ('AmbientLightSensor' in window) {
        try {
          const sensor = new (window as Window & { AmbientLightSensor?: new () => { illuminance: number; addEventListener: (type: string, cb: () => void) => void; start: () => void } }).AmbientLightSensor!()
          sensor.addEventListener('reading', () => {
            const lux = sensor.illuminance
            if (lux < 50) {
              this.detectDarkEnvironment()
            } else if (lux > 500) {
              this.detectBrightEnvironment()
            }
          })
          sensor.start()
        } catch {
          webglLogger.warn('[UniversalDesignSystem] Ambient light sensor not available')
        }
      }
    }

    // デバイス動き検出
    const checkDeviceMotion = () => {
      if ('DeviceMotionEvent' in window) {
        window.addEventListener('devicemotion', (event: DeviceMotionEvent) => {
          const acceleration = event.acceleration
          if (acceleration && Math.abs(acceleration.x || 0) > 5) {
            this.detectMobileMovement()
          }
        })
      }
    }

    checkScreenSize()
    checkAmbientLight()
    checkDeviceMotion()

    window.addEventListener('resize', checkScreenSize)
  }

  /**
   * ユーザープロファイルを作成
   */
  public createUserProfile(profile: Partial<UserProfile>): UserProfile {
    const defaultProfile: UserProfile = {
      id: profile.id || `user_${Date.now()}`,
      ageGroup: 'adult',
      techLevel: 'intermediate',
      motorAbility: 'full',
      visualAbility: 'full',
      auditoryAbility: 'full',
      cognitiveProfile: 'typical',
      deviceType: 'desktop',
      environment: 'quiet',
      language: 'ja',
      customSettings: new Map()
    }

    const userProfile = { ...defaultProfile, ...profile }
    this.userProfiles.set(userProfile.id, userProfile)

    return userProfile
  }

  /**
   * ユーザープロファイルに基づいてレイアウトを適応
   */
  public adaptToUser(userId: string): void {
    const profile = this.userProfiles.get(userId)
    if (!profile) return

    this.currentUserId = userId

    // プロファイルに最適なレイアウトを選択
    const bestLayout = this.findBestLayout(profile)
    if (bestLayout) {
      this.applyLayout(bestLayout)
    }

    // アクセシビリティ機能を自動有効化
    this.autoEnableAccessibilityFeatures(profile)

    this.notifyAdaptation('userAdaptation', { userId, profile, layout: bestLayout })
  }

  /**
   * 最適なレイアウトを検索
   */
  private findBestLayout(profile: UserProfile): AdaptiveLayout | null {
    let bestMatch: AdaptiveLayout | null = null
    let bestScore = 0

    const layoutsArray = Array.from(this.adaptiveLayouts.values());
    for (const layout of layoutsArray) {
      const score = this.calculateLayoutScore(layout, profile)
      if (score > bestScore) {
        bestScore = score
        bestMatch = layout
      }
    }

    return bestMatch
  }

  /**
   * レイアウトスコアを計算
   */
  private calculateLayoutScore(layout: AdaptiveLayout, profile: UserProfile): number {
    let score = 0

    // 直接マッチ
    if (layout.targetProfiles.includes(profile.ageGroup)) score += 10
    if (layout.targetProfiles.includes(profile.techLevel)) score += 8
    if (layout.targetProfiles.includes(profile.deviceType)) score += 15
    if (layout.targetProfiles.includes(profile.visualAbility)) score += 12
    if (layout.targetProfiles.includes(profile.motorAbility)) score += 10

    // 条件マッチ
    if (profile.ageGroup === 'senior' && layout.styles.fontSize >= 20) score += 5
    if (profile.visualAbility === 'low-vision' && layout.styles.contrastRatio >= 7) score += 8
    if (profile.deviceType === 'mobile' && layout.interactions.touchTargetSize >= 44) score += 6

    return score
  }

  /**
   * レイアウトを適用
   */
  private applyLayout(layout: AdaptiveLayout): void {
    this.currentLayout = layout

    // CSS変数を更新
    const root = document.documentElement
    root.style.setProperty('--ud-font-size', `${layout.styles.fontSize}px`)
    root.style.setProperty('--ud-line-height', layout.styles.lineHeight.toString())
    root.style.setProperty('--ud-spacing', `${layout.styles.spacing}px`)
    root.style.setProperty('--ud-border-width', `${layout.styles.borderWidth}px`)
    root.style.setProperty('--ud-animation-speed', layout.styles.animationSpeed.toString())
    root.style.setProperty('--ud-touch-target', `${layout.interactions.touchTargetSize}px`)

    // 色パレットを適用
    layout.styles.colorPalette.forEach((color, index) => {
      root.style.setProperty(`--ud-color-${index}`, color)
    })

    webglLogger.info(`[UniversalDesignSystem] Applied layout: ${layout.id}`)
  }

  /**
   * アクセシビリティ機能を自動有効化
   */
  private autoEnableAccessibilityFeatures(profile: UserProfile): void {
    const conditions: string[] = [
      profile.ageGroup,
      profile.visualAbility,
      profile.auditoryAbility,
      profile.motorAbility,
      profile.cognitiveProfile
    ]

    const featuresArray = Array.from(this.accessibilityFeatures.values());
    for (const feature of featuresArray) {
      const shouldEnable = feature.targetConditions.some(condition =>
        conditions.includes(condition)
      )

      if (shouldEnable) {
        feature.enable()
        webglLogger.info(`[UniversalDesignSystem] Auto-enabled feature: ${feature.id}`)
      }
    }
  }

  /**
   * マルチモーダルフィードバックを提供
   */
  public provideMultimodalFeedback(feedback: MultimodalFeedback): void {
    // 視覚フィードバック
    if (feedback.visual) {
      this.provideVisualFeedback(feedback.visual)
    }

    // 音響フィードバック
    if (feedback.audio) {
      this.provideAudioFeedback(feedback.audio)
    }

    // 触覚フィードバック
    if (feedback.haptic && 'vibrate' in navigator) {
      navigator.vibrate(feedback.haptic.pattern || [100])
    }
  }

  /**
   * 色コントラスト比を計算
   */
  public calculateContrastRatio(color1: string, color2: string): number {
    const luminance1 = this.getRelativeLuminance(color1)
    const luminance2 = this.getRelativeLuminance(color2)

    const lighter = Math.max(luminance1, luminance2)
    const darker = Math.min(luminance1, luminance2)

    return (lighter + 0.05) / (darker + 0.05)
  }

  /**
   * 相対輝度を計算
   */
  private getRelativeLuminance(color: string): number {
    // HEX形式をRGBに変換
    let r, g, b
    if (color.startsWith('#')) {
      const hex = color.replace('#', '')
      r = parseInt(hex.substring(0, 2), 16) / 255
      g = parseInt(hex.substring(2, 4), 16) / 255
      b = parseInt(hex.substring(4, 6), 16) / 255
    } else {
      // 他の色形式の処理（簡略化）
      return 0.5
    }

    // sRGBを線形RGBに変換
    r = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
    g = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
    b = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)

    // 相対輝度を計算
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }

  /**
   * 色コントラストがWCAG 2.1 AA基準を満たすか検証
   */
  public validateContrastRatio(color1: string, color2: string, fontSize: number = 16, isBold: boolean = false): boolean {
    const ratio = this.calculateContrastRatio(color1, color2)

    // フォントサイズと太さに基づく基準
    if (fontSize >= 18 || (fontSize >= 14 && isBold)) {
      // 大きな文字（AA基準: 3:1）
      return ratio >= 3.0
    } else {
      // 通常の文字（AA基準: 4.5:1）
      return ratio >= 4.5
    }
  }

  /**
   * 現在のカラーパレットのコントラスト比を検証
   */
  public validateCurrentPaletteContrast(fontSize: number = 16, isBold: boolean = false): boolean[] {
    if (!this.currentLayout) return [];

    const palette = this.currentLayout.styles.colorPalette;
    const results: boolean[] = [];

    // 各色の組み合わせを検証
    for (let i = 0; i < palette.length; i++) {
      for (let j = i + 1; j < palette.length; j++) {
        const isValid = this.validateContrastRatio(palette[i], palette[j], fontSize, isBold);
        results.push(isValid);
      }
    }

    return results;
  }

  /**
   * 視覚フィードバックを提供
   */
  private provideVisualFeedback(visual: NonNullable<MultimodalFeedback['visual']>): void {
    if (visual.animation) {
      // アニメーション実行
      document.documentElement.style.setProperty('--feedback-animation', visual.animation)
    }

    if (visual.color) {
      // 一時的な色変更
      document.documentElement.style.setProperty('--feedback-color', visual.color)
      setTimeout(() => {
        document.documentElement.style.removeProperty('--feedback-color')
      }, 300)
    }
  }

  /**
   * 音響フィードバックを提供
   */
  private provideAudioFeedback(audio: NonNullable<MultimodalFeedback['audio']>): void {
    if (audio.sound) {
      // 効果音再生
      const audioElement = new Audio(audio.sound)
      audioElement.volume = audio.volume || 0.5
      audioElement.play().catch(error => {
        webglLogger.warn('[UniversalDesignSystem] Audio feedback failed:', error)
      })
    }

    if (audio.speech && 'speechSynthesis' in window) {
      // 音声合成
      const utterance = new SpeechSynthesisUtterance(audio.speech)
      utterance.volume = audio.volume || 0.7
      window.speechSynthesis.speak(utterance)
    }
  }

  /**
   * 環境検出メソッド群
   */
  private detectMobileEnvironment(): void {
    this.notifyEnvironmentChange('mobile')
  }

  private detectDarkEnvironment(): void {
    this.notifyEnvironmentChange('dark')
  }

  private detectBrightEnvironment(): void {
    this.notifyEnvironmentChange('bright')
  }

  private detectHighResolutionEnvironment(): void {
    this.notifyEnvironmentChange('high-resolution')
  }

  private detectMobileMovement(): void {
    this.notifyEnvironmentChange('mobile-movement')
  }

  /**
   * 環境変化を通知
   */
  private notifyEnvironmentChange(environment: string): void {
    this.notifyAdaptation('environmentChange', { environment })
  }

  /**
   * アクセシビリティ機能実装メソッド群
   */
  private enableVoiceNavigation(): void {
    webglLogger.info('[UniversalDesignSystem] Voice navigation enabled')
  }

  private disableVoiceNavigation(): void {
    webglLogger.info('[UniversalDesignSystem] Voice navigation disabled')
  }

  private configureVoiceNavigation(options: Record<string, unknown>): void {
    webglLogger.info('[UniversalDesignSystem] Voice navigation configured:', options)
  }

  private enableHighContrast(): void {
    document.documentElement.classList.add('high-contrast')
  }

  private disableHighContrast(): void {
    document.documentElement.classList.remove('high-contrast')
  }

  private configureHighContrast(options: Record<string, unknown>): void {
    webglLogger.info('[UniversalDesignSystem] High contrast configured:', options)
  }

  private enableLargeText(): void {
    document.documentElement.classList.add('large-text')
  }

  private disableLargeText(): void {
    document.documentElement.classList.remove('large-text')
  }

  private configureLargeText(options: Record<string, unknown>): void {
    webglLogger.info('[UniversalDesignSystem] Large text configured:', options)
  }

  private enableSlowAnimations(): void {
    document.documentElement.classList.add('slow-animations')
  }

  private disableSlowAnimations(): void {
    document.documentElement.classList.remove('slow-animations')
  }

  private configureSlowAnimations(options: Record<string, unknown>): void {
    webglLogger.info('[UniversalDesignSystem] Slow animations configured:', options)
  }

  private enableHapticFeedback(): void {
    webglLogger.info('[UniversalDesignSystem] Haptic feedback enabled')
  }

  private disableHapticFeedback(): void {
    webglLogger.info('[UniversalDesignSystem] Haptic feedback disabled')
  }

  private configureHapticFeedback(options: Record<string, unknown>): void {
    webglLogger.info('[UniversalDesignSystem] Haptic feedback configured:', options)
  }

  /**
   * 適応レイアウトを追加
   */
  public addAdaptiveLayout(layout: AdaptiveLayout): void {
    this.adaptiveLayouts.set(layout.id, layout)
  }

  /**
   * アクセシビリティ機能を追加
   */
  public addAccessibilityFeature(feature: AccessibilityFeature): void {
    this.accessibilityFeatures.set(feature.id, feature)
  }

  /**
   * 適応通知
   */
  private notifyAdaptation(type: string, data: Record<string, unknown>): void {
    this.callbacks.forEach(callback => {
      try {
        callback({ type, ...data })
      } catch (error) {
        webglLogger.error('[UniversalDesignSystem] Callback error:', error)
      }
    })
  }

  /**
   * コールバックを登録
   */
  public onAdaptation(id: string, callback: (data: unknown) => void): void {
    this.callbacks.set(id, callback)
  }

  /**
   * 現在の設定を取得
   */
  public getCurrentConfiguration(): { userId: string | null; layout: AdaptiveLayout | null; userProfile: unknown } {
    return {
      userId: this.currentUserId,
      layout: this.currentLayout,
      userProfile: this.currentUserId ? this.userProfiles.get(this.currentUserId) : null
    }
  }

  /**
   * 適応レイアウトを取得
   */
  public getAdaptiveLayout(id: string): AdaptiveLayout | undefined {
    return this.adaptiveLayouts.get(id);
  }

  /**
   * システムを破棄
   */
  public dispose(): void {
    this.userProfiles.clear()
    this.adaptiveLayouts.clear()
    this.accessibilityFeatures.clear()
    this.callbacks.clear()

    webglLogger.info('[UniversalDesignSystem] Disposed')
  }
}